/**
 * Engine chrome kit — shared HP bar create/update (Phaser presentation).
 * Visibility rules (always vs damaged-only) stay call-site options.
 * Damage-delta flash: white/cream ghost trail behind live fill (default ON).
 */
import type Phaser from "phaser";
import {
  HP_DAMAGE_FLASH_DEFAULTS,
  createHpDamageFlashCtrl,
  resetHpDamageFlash,
  stepHpDamageFlash,
  type HpDamageFlashCtrl,
} from "./hp-damage-flash";

export type HpBarParts = {
  bg: Phaser.GameObjects.Rectangle;
  /** Damage-delta trail (behind live fill). Always created; may be hidden. */
  ghost: Phaser.GameObjects.Rectangle;
  fill: Phaser.GameObjects.Rectangle;
};

export type CreateHpBarOpts = {
  barW: number;
  barH: number;
  /** Local Y of bar center (negative = above body). */
  y: number;
  bgColor: number;
  fillColor: number;
  bgAlpha?: number;
  /** Initial fill ratio (default 1). Seeds flash baseline. */
  initialRatio?: number;
  /** Damage flash color (default cream/white). */
  damageFlashColor?: number;
  /** When false, no ghost trail. Default true. */
  damageFlash?: boolean;
  damageFlashMs?: number;
};

export type UpdateHpBarOpts = {
  ratio: number;
  /** When false, hide both. When true, show. */
  visible: boolean;
  fillColor?: number;
  barW?: number;
  barH?: number;
  /** Override per-update; omit to keep create-time setting. */
  damageFlash?: boolean;
  damageFlashMs?: number;
  damageFlashColor?: number;
};

type HpBarRuntime = {
  ctrl: HpDamageFlashCtrl;
  damageFlash: boolean;
  damageFlashMs: number;
  damageFlashColor: number;
  barW: number;
  barH: number;
};

const runtimeByFill = new WeakMap<Phaser.GameObjects.Rectangle, HpBarRuntime>();

export function createHpBar(
  scene: Phaser.Scene,
  opts: CreateHpBarOpts,
): HpBarParts {
  const {
    barW,
    barH,
    y,
    bgColor,
    fillColor,
    bgAlpha = 0.8,
    initialRatio = 1,
    damageFlash = HP_DAMAGE_FLASH_DEFAULTS.enabled,
    damageFlashMs = HP_DAMAGE_FLASH_DEFAULTS.durationMs,
    damageFlashColor = HP_DAMAGE_FLASH_DEFAULTS.color,
  } = opts;
  const r = Math.max(0, Math.min(1, initialRatio));
  const bg = scene.add
    .rectangle(0, y, barW, barH, bgColor, bgAlpha)
    .setOrigin(0.5);
  const ghost = scene.add
    .rectangle(-barW / 2, y, barW * r, barH, damageFlashColor)
    .setOrigin(0, 0.5)
    .setVisible(false);
  const fill = scene.add
    .rectangle(-barW / 2, y, barW * r, barH, fillColor)
    .setOrigin(0, 0.5);
  runtimeByFill.set(fill, {
    ctrl: createHpDamageFlashCtrl(r),
    damageFlash,
    damageFlashMs,
    damageFlashColor,
    barW,
    barH,
  });
  return { bg, ghost, fill };
}

export function updateHpBar(parts: HpBarParts, opts: UpdateHpBarOpts): void {
  const r = Math.max(0, Math.min(1, opts.ratio));
  const rt = runtimeByFill.get(parts.fill);
  const barW = opts.barW ?? rt?.barW ?? parts.bg.displayWidth;
  const barH = opts.barH ?? rt?.barH ?? parts.bg.displayHeight;
  if (rt) {
    if (opts.barW != null) rt.barW = barW;
    if (opts.barH != null) rt.barH = barH;
    if (opts.damageFlash != null) rt.damageFlash = opts.damageFlash;
    if (opts.damageFlashMs != null) rt.damageFlashMs = opts.damageFlashMs;
    if (opts.damageFlashColor != null) {
      rt.damageFlashColor = opts.damageFlashColor;
      parts.ghost.setFillStyle(opts.damageFlashColor);
    }
  }
  if (opts.barW != null || opts.barH != null) {
    parts.bg.setDisplaySize(barW, barH);
  }

  const nowMs = parts.fill.scene?.time?.now ?? performance.now();
  let live = r;
  let ghostRatio = r;
  let showGhost = false;
  if (rt) {
    const step = stepHpDamageFlash(rt.ctrl, r, nowMs, {
      enabled: rt.damageFlash && (opts.damageFlash !== false),
      durationMs: opts.damageFlashMs ?? rt.damageFlashMs,
    });
    live = step.live;
    ghostRatio = step.ghost;
    showGhost = step.showGhost;
  }

  parts.fill.setDisplaySize(barW * live, barH);
  parts.fill.setPosition(-barW / 2, parts.bg.y);
  if (opts.fillColor != null) {
    parts.fill.setFillStyle(opts.fillColor);
  }

  parts.ghost.setPosition(-barW / 2, parts.bg.y);
  parts.ghost.setDisplaySize(barW * ghostRatio, barH);

  parts.bg.setVisible(opts.visible);
  parts.fill.setVisible(opts.visible);
  parts.ghost.setVisible(opts.visible && showGhost);
}

/** Hide chrome without advancing flash state (death / cull). */
export function setHpBarVisible(parts: HpBarParts, visible: boolean): void {
  parts.bg.setVisible(visible);
  parts.fill.setVisible(visible);
  if (!visible) {
    parts.ghost.setVisible(false);
  }
}

/** Respawn / bar swap — reset damage flash baseline. */
export function resetHpBarFlash(parts: HpBarParts, ratio = 1): void {
  const rt = runtimeByFill.get(parts.fill);
  if (rt) resetHpDamageFlash(rt.ctrl, ratio);
  parts.ghost.setVisible(false);
}
